import React, { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { CSSObjectWithLabel, ControlProps } from 'react-select'
import AsyncSelect from 'react-select/async'

import {
  localDateTimeToUTC,
  normalizeToUTC,
  utcToLocalDateTime,
} from './dateUtils'
import { FilterValueInputProps } from './types'
import { useFilterConditionContext } from './useFilterCondition'
import { FieldLabel } from '@/Atomic/Atoms/FieldLabel'
import { BasicInput } from '@/Atomic/Atoms/Inputs/BasicInput'
import { BasicSelect } from '@/Atomic/Atoms/Inputs/BasicSelect'

/**
 * FilterValueInput - Handles value input for filter conditions
 * Renders appropriate input based on field type and operator
 */
export const FilterValueInput: React.FC<FilterValueInputProps> = () => {
  const { t } = useTranslation()
  const {
    condition,
    isFirstRow,
    inputType,
    attribute,
    entityDataClass,
    referenceDataClass,
    loadOptions,
    loadReferenceOptions,
    reactAsyncSelectStyle,
    referenceAsyncSelectStyle,
    hasValueError,
    handleValueChange,
    handleClearValueError,
  } = useFilterConditionContext()

  // Deduplicated enum options
  const deduplicatedEnumOptions = useMemo(() => {
    if (!attribute?.enumValues) return []
    const titleMap = new Map<string, string[]>()
    for (const ev of attribute.enumValues) {
      if (!titleMap.has(ev.title)) titleMap.set(ev.title, [])
      titleMap.get(ev.title)!.push(ev.value)
    }
    return Array.from(titleMap.entries()).map(([title, allValues]) => ({
      value: title,
      label: title,
      allValues,
    }))
  }, [attribute])

  const getSingleRecordValue = () => {
    if (typeof condition.value === 'string' && condition.value) {
      return {
        value: condition.value,
        label: condition.label || condition.value,
      }
    }

    if (Array.isArray(condition.value) && condition.value.length > 0) {
      const labelParts = condition.label?.split(', ') || []
      const firstValue = condition.value[0] as string
      return {
        value: firstValue,
        label: labelParts[0] || firstValue,
      }
    }

    return null
  }

  // Render input based on type
  const renderInput = () => {
    switch (inputType) {
      case 'none':
        return (
          <BasicInput
            name={`value-${condition.id}`}
            type="text"
            placeholder={t('filter.value')}
            value=""
            disabled
          />
        )

      case 'boolean':
        return (
          <BasicSelect
            key={`value-${condition.id}-${condition.operator}`}
            name={`value-${condition.id}`}
            placeholder={t('filter.select_value')}
            hasError={hasValueError}
            options={[
              { value: 'true', label: t('common.true') },
              { value: 'false', label: t('common.false') },
            ]}
            value={[
              { value: 'true', label: t('common.true') },
              { value: 'false', label: t('common.false') },
            ].find((opt) => opt.value === String(condition.value))}
            onChange={(option) =>
              handleValueChange(
                (option as { value: string } | null)?.value ?? null,
              )
            }
            onFocus={handleClearValueError}
            isClearable={false}
          />
        )

      case 'select':
        return attribute?.enumValues ? (
          <BasicSelect
            key={`value-${condition.id}-${condition.operator}`}
            name={`value-${condition.id}`}
            placeholder={t('filter.select_value')}
            hasError={hasValueError}
            options={attribute.enumValues.map((ev) => ({
              value: ev.value,
              label: ev.title,
            }))}
            value={attribute.enumValues
              .map((ev) => ({
                value: ev.value,
                label: ev.title,
              }))
              .find((opt) => opt.value === condition.value)}
            onChange={(option) =>
              handleValueChange(
                (option as { value: string } | null)?.value ?? null,
              )
            }
            onFocus={handleClearValueError}
          />
        ) : null

      case 'multi-select':
        return attribute?.enumValues ? (
          <BasicSelect
            key={`value-${condition.id}-${condition.operator}`}
            name={`value-${condition.id}`}
            placeholder={t('filter.select_values')}
            hasError={hasValueError}
            options={deduplicatedEnumOptions}
            isMulti
            value={deduplicatedEnumOptions.filter(
              (opt) =>
                Array.isArray(condition.value) &&
                opt.allValues.some((id) =>
                  (condition.value as string[]).includes(id),
                ),
            )}
            onChange={(options) => {
              const selected = options as
                | { value: string; allValues: string[] }[]
                | null
              handleValueChange(
                selected ? selected.flatMap((o) => o.allValues) : [],
              )
            }}
            onFocus={handleClearValueError}
          />
        ) : null

      case 'entity-selector':
        return entityDataClass ? (
          <AsyncSelect
            key={`value-${condition.id}-${condition.operator}`}
            className={hasValueError ? 'is-invalid' : ''}
            loadOptions={loadOptions}
            isMulti={false}
            onChange={(option) => {
              const selectedOption = option as {
                value: string
                label: string
              } | null
              if (!selectedOption) {
                handleValueChange(null, undefined)
              } else {
                handleValueChange(selectedOption.value, selectedOption.label)
              }
            }}
            onFocus={handleClearValueError}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            value={getSingleRecordValue()}
            placeholder={t('filter.select_records')}
            styles={{
              ...reactAsyncSelectStyle,
              control: (
                base: CSSObjectWithLabel,
                state: ControlProps<{ value: string; label: string }>,
              ) => ({
                ...(typeof reactAsyncSelectStyle.control === 'function'
                  ? reactAsyncSelectStyle.control(base, state)
                  : base),
                borderColor: hasValueError ? '#dc3545' : base.borderColor,
              }),
            }}
            isClearable
          />
        ) : null

      case 'entity-selector-multi':
        return entityDataClass ? (
          <AsyncSelect
            key={`value-${condition.id}-${condition.operator}`}
            className={hasValueError ? 'is-invalid' : ''}
            loadOptions={loadOptions}
            isMulti={true as boolean}
            onChange={(options) => {
              const selectedOptions = options as
                | { value: string; label: string }[]
                | null
              if (!selectedOptions || selectedOptions.length === 0) {
                handleValueChange([], undefined)
              } else {
                const values = selectedOptions.map((opt) => opt.value)
                const labels = selectedOptions
                  .map((opt) => opt.label)
                  .join(', ')
                handleValueChange(values, labels)
              }
            }}
            onFocus={handleClearValueError}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            value={
              Array.isArray(condition.value) && condition.value.length > 0
                ? condition.value.map((val, idx) => {
                    const labelParts = condition.label?.split(', ') || []
                    return {
                      value: val as string,
                      label: labelParts[idx] || (val as string),
                    }
                  })
                : []
            }
            placeholder={t('filter.select_records')}
            styles={{
              ...reactAsyncSelectStyle,
              control: (
                base: CSSObjectWithLabel,
                state: ControlProps<{ value: string; label: string }>,
              ) => ({
                ...(typeof reactAsyncSelectStyle.control === 'function'
                  ? reactAsyncSelectStyle.control(base, state)
                  : base),
                borderColor: hasValueError ? '#dc3545' : base.borderColor,
              }),
            }}
            isClearable
          />
        ) : null

      case 'reference-selector':
        return referenceDataClass ? (
          <AsyncSelect
            key={`value-${condition.id}-${condition.operator}`}
            className={hasValueError ? 'is-invalid' : ''}
            loadOptions={loadReferenceOptions}
            isMulti={false}
            onChange={(option) => {
              const selectedOption = option as {
                value: string
                label: string
              } | null
              if (!selectedOption) {
                handleValueChange(null, undefined)
              } else {
                handleValueChange(selectedOption.value, selectedOption.label)
              }
            }}
            onFocus={handleClearValueError}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            value={getSingleRecordValue()}
            placeholder={t('filter.select_records')}
            styles={{
              ...referenceAsyncSelectStyle,
              control: (
                base: CSSObjectWithLabel,
                state: ControlProps<{ value: string; label: string }>,
              ) => ({
                ...(typeof referenceAsyncSelectStyle.control === 'function'
                  ? referenceAsyncSelectStyle.control(base, state)
                  : base),
                borderColor: hasValueError ? '#dc3545' : base.borderColor,
              }),
            }}
            isClearable
          />
        ) : null

      case 'reference-selector-multi':
        return referenceDataClass ? (
          <AsyncSelect
            key={`value-${condition.id}-${condition.operator}`}
            className={hasValueError ? 'is-invalid' : ''}
            loadOptions={loadReferenceOptions}
            isMulti={true as boolean}
            onChange={(options) => {
              const selectedOptions = options as
                | { value: string; label: string }[]
                | null
              if (!selectedOptions || selectedOptions.length === 0) {
                handleValueChange([], undefined)
              } else {
                const values = selectedOptions.map((opt) => opt.value)
                const labels = selectedOptions
                  .map((opt) => opt.label)
                  .join(', ')
                handleValueChange(values, labels)
              }
            }}
            onFocus={handleClearValueError}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            value={
              Array.isArray(condition.value) && condition.value.length > 0
                ? condition.value.map((val, idx) => {
                    const labelParts = condition.label?.split(', ') || []
                    return {
                      value: val as string,
                      label: labelParts[idx] || (val as string),
                    }
                  })
                : []
            }
            placeholder={t('filter.select_records')}
            styles={{
              ...referenceAsyncSelectStyle,
              control: (
                base: CSSObjectWithLabel,
                state: ControlProps<{ value: string; label: string }>,
              ) => ({
                ...(typeof referenceAsyncSelectStyle.control === 'function'
                  ? referenceAsyncSelectStyle.control(base, state)
                  : base),
                borderColor: hasValueError ? '#dc3545' : base.borderColor,
              }),
            }}
            isClearable
          />
        ) : null

      case 'datetime':
        return condition.operator === 'between' ? (
          <div className="between-inputs-wrapper">
            <BasicInput
              key={`value-from-${condition.id}-${condition.operator}`}
              name={`value-from-${condition.id}`}
              inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
              type="datetime-local"
              placeholder={t('filter.from')}
              value={
                Array.isArray(condition.value)
                  ? utcToLocalDateTime(condition.value[0] || '')
                  : ''
              }
              onChange={(e) => {
                let value = e.target.value
                if (value && !value.includes('T')) {
                  value = `${value}T00:00`
                }
                const utcValue = value ? localDateTimeToUTC(value) : ''
                const currentToValue = Array.isArray(condition.value)
                  ? normalizeToUTC(condition.value[1] || '')
                  : ''
                handleValueChange([utcValue, currentToValue])
              }}
              onFocus={handleClearValueError}
              disabled={!condition.field}
            />
            <span className="separator">-</span>
            <BasicInput
              key={`value-to-${condition.id}-${condition.operator}`}
              name={`value-to-${condition.id}`}
              inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
              type="datetime-local"
              placeholder={t('filter.to')}
              value={
                Array.isArray(condition.value)
                  ? utcToLocalDateTime(condition.value[1] || '')
                  : ''
              }
              onChange={(e) => {
                let value = e.target.value
                if (value && !value.includes('T')) {
                  value = `${value}T00:00`
                }
                const utcValue = value ? localDateTimeToUTC(value) : ''
                const currentFromValue = Array.isArray(condition.value)
                  ? normalizeToUTC(condition.value[0] || '')
                  : ''
                handleValueChange([currentFromValue, utcValue])
              }}
              onFocus={handleClearValueError}
              disabled={!condition.field}
            />
          </div>
        ) : (
          <BasicInput
            key={`value-${condition.id}-${condition.operator}`}
            name={`value-${condition.id}`}
            inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
            type="datetime-local"
            placeholder={t('filter.value')}
            value={utcToLocalDateTime((condition.value as string) || '')}
            onChange={(e) => {
              let value = e.target.value
              if (value && !value.includes('T')) {
                value = `${value}T00:00`
              }
              const utcValue = value ? localDateTimeToUTC(value) : ''
              handleValueChange(utcValue)
            }}
            onFocus={handleClearValueError}
            disabled={!condition.field}
          />
        )

      case 'date':
        return condition.operator === 'between' ? (
          <div className="between-inputs-wrapper">
            <BasicInput
              key={`value-from-${condition.id}-${condition.operator}`}
              name={`value-from-${condition.id}`}
              inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
              type="date"
              placeholder={t('filter.from')}
              value={
                Array.isArray(condition.value) ? condition.value[0] || '' : ''
              }
              onChange={(e) => {
                const value = e.target.value
                const currentToValue = Array.isArray(condition.value)
                  ? condition.value[1]
                  : ''
                handleValueChange([value, currentToValue || ''])
              }}
              onFocus={handleClearValueError}
              disabled={!condition.field}
            />
            <span className="separator">-</span>
            <BasicInput
              key={`value-to-${condition.id}-${condition.operator}`}
              name={`value-to-${condition.id}`}
              inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
              type="date"
              placeholder={t('filter.to')}
              value={
                Array.isArray(condition.value) ? condition.value[1] || '' : ''
              }
              onChange={(e) => {
                const value = e.target.value
                const currentFromValue = Array.isArray(condition.value)
                  ? condition.value[0]
                  : ''
                handleValueChange([currentFromValue || '', value])
              }}
              onFocus={handleClearValueError}
              disabled={!condition.field}
            />
          </div>
        ) : (
          <BasicInput
            key={`value-${condition.id}-${condition.operator}`}
            name={`value-${condition.id}`}
            inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
            type="date"
            placeholder={t('filter.value')}
            value={(condition.value as string) || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            onFocus={handleClearValueError}
            disabled={!condition.field}
          />
        )

      case 'time-range':
        return (
          <div className="time-range-inputs-wrapper d-flex gap-2">
            <BasicInput
              key={`value-quantity-${condition.id}-${condition.operator}`}
              name={`value-quantity-${condition.id}`}
              inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
              type="number"
              min="1"
              step="1"
              placeholder={t('filter.quantity')}
              value={
                condition.value &&
                typeof condition.value === 'object' &&
                'amount' in condition.value
                  ? String(condition.value.amount)
                  : ''
              }
              onChange={(e) => {
                const amount = e.target.value
                  ? parseInt(e.target.value, 10)
                  : null
                const unit =
                  condition.value &&
                  typeof condition.value === 'object' &&
                  'unit' in condition.value
                    ? condition.value.unit
                    : 'days'
                handleValueChange(amount ? { amount, unit } : null)
              }}
              onFocus={handleClearValueError}
              disabled={!condition.field}
            />
            <BasicSelect
              key={`value-unit-${condition.id}-${condition.operator}`}
              name={`value-unit-${condition.id}`}
              placeholder={t('filter.timeUnit')}
              hasError={hasValueError}
              options={[
                { value: 'days', label: t('filter.timeUnits.days') },
                { value: 'weeks', label: t('filter.timeUnits.weeks') },
                { value: 'months', label: t('filter.timeUnits.months') },
                { value: 'years', label: t('filter.timeUnits.years') },
              ]}
              value={
                condition.value &&
                typeof condition.value === 'object' &&
                'unit' in condition.value
                  ? {
                      value: condition.value.unit,
                      label: t(`filter.timeUnits.${condition.value.unit}`),
                    }
                  : { value: 'days', label: t('filter.timeUnits.days') }
              }
              onChange={(option) => {
                const amount =
                  condition.value &&
                  typeof condition.value === 'object' &&
                  'amount' in condition.value
                    ? condition.value.amount
                    : 1
                const unit =
                  (option as { value: string } | null)?.value || 'days'
                handleValueChange({ amount, unit })
              }}
              onFocus={handleClearValueError}
              isClearable={false}
              isDisabled={!condition.field}
            />
          </div>
        )

      case 'number':
      case 'text':
      default:
        return (
          <BasicInput
            key={`value-${condition.id}-${condition.operator}`}
            name={`value-${condition.id}`}
            inputWrapperClassName={hasValueError ? 'is-invalid' : ''}
            type={inputType === 'number' ? 'number' : 'text'}
            placeholder={t('filter.value')}
            value={(condition.value as string) || ''}
            onChange={(e) => handleValueChange(e.target.value)}
            onBlur={(e) => handleValueChange(e.target.value.trim())}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
              }
            }}
            onFocus={handleClearValueError}
            disabled={!condition.field}
          />
        )
    }
  }

  return (
    <div className="form-field value-input">
      {isFirstRow && (
        <FieldLabel name={`value-${condition.id}`} label={t('filter.value')} />
      )}
      {renderInput()}
    </div>
  )
}
